import Location from '@/models/location';
import type { ItineraryImportDraftPlace, ItineraryImportLocation } from '@/models/itineraryImportTypes';
import { extractLocationCoordinates } from '@/utils/locationCoordinates';
import { extractPhotosFromLocation } from '@/utils/photoUtils';
import { determineSlotType } from '@/utils/slotTypeDeterminer';
import { resolveGtCategory, type GtCategory } from '@/utils/gtCategory';

/** Travel rows and the old Add-form flight type are not Google places. */
export function isTravelSlotType(slotType?: string | null): boolean {
  if (!slotType) return false;
  const normalized = slotType.trim().toLowerCase();
  return normalized === 'travel' || normalized === 'flight' || normalized === 'flights';
}

/**
 * Slot type from a saved post. Keeps `travel` when the API returns it.
 * Legacy `flight` from the old Add form is stored as `travel`.
 * Missing category falls back to `do`.
 */
export function persistedSlotType(
  gtCategory?: string | null,
  slotType?: string | null,
): string {
  const values = [gtCategory, slotType].map((value) => (value || '').trim().toLowerCase());
  if (values.some((value) => value === 'travel' || value === 'flight' || value === 'flights')) {
    return 'travel';
  }
  const raw = values.find((value) => value.length > 0) ?? '';
  if (!raw) return 'do';
  return raw;
}

export function addressFromLocation(location: Location): string {
  const formatted = location.google_places_data?.formattedAddress;
  if (typeof formatted === 'string' && formatted.trim()) return formatted.trim();
  if (typeof location.a3_intl_address === 'string' && location.a3_intl_address.trim()) {
    return location.a3_intl_address.trim();
  }
  if (typeof location.address === 'string' && location.address.trim()) {
    return location.address.trim();
  }
  return '';
}

export function photosFromLocation(location: Location): string[] {
  return extractPhotosFromLocation(location, 400);
}

/** Eat / stay / do from a picked Google place. Transit types stay `do`, not travel. */
export function slotTypeFromPickedPlace(location: Location): GtCategory {
  const fromTypes = resolveGtCategory(location);
  if (fromTypes !== 'do') return fromTypes;
  const fromName = determineSlotType(location);
  if (fromName === 'eat' || fromName === 'stay') return fromName;
  return 'do';
}

export interface UpgradedCustomSlotFields {
  isUserCreated: false;
  placeId?: string;
  address: string;
  location: { lat: number; lng: number };
  image_urls: string[];
  image?: string;
  images: string[];
  slotType: GtCategory;
  name: string;
  title: string;
  locationData: Record<string, unknown>;
}

function locationDataFromPlace(location: Location, slotType: GtCategory, address: string): Record<string, unknown> {
  const coords = extractLocationCoordinates(location);
  const hasCoords = coords.lat !== 0 || coords.lng !== 0;
  return {
    ...(location as unknown as Record<string, unknown>),
    _id: location._id || undefined,
    name: location.name,
    google_place_id: location.google_place_id,
    gt_category: slotType,
    a3_intl_address: address || location.a3_intl_address,
    address: address || location.address,
    geoposition: hasCoords
      ? { type: 'Point', coordinates: [coords.lng, coords.lat] }
      : location.geoposition,
    is_user_created: false,
  };
}

/** Fields a Google pick writes onto a custom stop. Does not touch notes, time, or day index. */
export function slotFieldsFromPickedPlace(pickedPlace: Location): UpgradedCustomSlotFields {
  const slotType = slotTypeFromPickedPlace(pickedPlace);
  const address = addressFromLocation(pickedPlace);
  const imageUrls = photosFromLocation(pickedPlace);
  const name = pickedPlace.name?.trim() || 'Place';
  return {
    isUserCreated: false,
    placeId: pickedPlace.google_place_id,
    address,
    location: extractLocationCoordinates(pickedPlace),
    image_urls: imageUrls,
    image: imageUrls[0],
    images: imageUrls,
    slotType,
    name,
    title: name,
    locationData: locationDataFromPlace(pickedPlace, slotType, address),
  };
}

function existingActivityName(slot: { name?: unknown; title?: unknown }): string {
  const name = typeof slot.name === 'string' ? slot.name.trim() : '';
  if (name) return name;
  const title = typeof slot.title === 'string' ? slot.title.trim() : '';
  return title;
}

/**
 * Title for a Google-backed stop. An activity name that is already set stays,
 * including a tour title that is not the meeting place. A blank title uses the
 * Google name. Notes are not used as the title.
 */
export function displayTitleForGoogleStop(
  activityName: string | null | undefined,
  googleName: string | null | undefined,
  notes?: string | null,
): string {
  const activity = (activityName || '').trim();
  const google = (googleName || '').trim();
  const note = (notes || '').trim();
  if (activity && activity !== google && activity !== note) return activity;
  return google || activity || 'Untitled stop';
}

/**
 * Upgrade a custom stop after a Google place pick.
 * Travel rows are not upgraded. Notes, time, day index, and an existing
 * activity name are kept. Photo and category still come from the picked place.
 */
export function upgradeCustomSlotFromPlace<T extends { slotType?: string | null }>(
  slot: T,
  pickedPlace: Location,
): (T & UpgradedCustomSlotFields) | null {
  if (isTravelSlotType(slot.slotType)) return null;
  const fields = slotFieldsFromPickedPlace(pickedPlace);
  const activityName = existingActivityName(slot);
  return {
    ...slot,
    ...fields,
    ...(activityName ? { name: activityName, title: activityName } : {}),
  };
}

/** Replace an unmatched import row's location so the existing Google save path runs. */
export function applyGoogleMatchToImportPlace(
  place: ItineraryImportDraftPlace,
  pickedPlace: Location,
): ItineraryImportDraftPlace | null {
  if (place.row_kind === 'travel') return null;
  const category = slotTypeFromPickedPlace(pickedPlace);
  const address = addressFromLocation(pickedPlace);
  const coords = extractLocationCoordinates(pickedPlace);
  const hasCoords = coords.lat !== 0 || coords.lng !== 0;
  const photoNames = (pickedPlace.photos ?? [])
    .map((photo) => photo.name || photo.photo_reference)
    .filter((name): name is string => typeof name === 'string' && name.length > 0);

  const location: ItineraryImportLocation = {
    _id: pickedPlace._id || pickedPlace.google_place_id || place.client_id,
    ...(pickedPlace.google_place_id ? { google_place_id: pickedPlace.google_place_id } : {}),
    name: pickedPlace.name?.trim() || place.name,
    ...(address ? { address } : {}),
    ...(hasCoords
      ? { geoposition: { type: 'Point', coordinates: [coords.lng, coords.lat] } }
      : {}),
    ...(photoNames.length > 0 ? { photos: photoNames } : {}),
    gt_category: category,
  };

  return {
    ...place,
    category,
    match_confidence: 'high',
    match_reason: null,
    location,
  };
}
